import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { notificationsAPI } from '../../services/api';
import { useSSE } from '../../hooks/useSSE';
import OkcHealthIndicator from '../OkcHealthIndicator';

// Active ÖKC device id — Settings sayfası bunu localStorage'a yazar.
// AuthContext'e taşınana kadar localStorage tek doğruluk kaynağı.
const OKC_ACTIVE_DEVICE_KEY = 'restaflex_active_okc_device';
function getActiveOkcDeviceId() {
  try { return localStorage.getItem(OKC_ACTIVE_DEVICE_KEY) || null; } catch { return null; }
}

// pageTitles route → i18n key (i18n çözümü Header bileşeni içinde, runtime'da)
const PAGE_TITLE_KEYS = {
  '/': 'nav_dashboard',
  '/tables': 'pageTableManagement',
  '/orders': 'pageOrderManagement',
  '/menu-management': 'pageMenuManagement',
  '/kitchen': 'pageKitchenScreen',
  '/stock': 'pageStockManagement',
  '/recipes': 'pageRecipeSystem',
  '/payments': 'pagePaymentsCash',
  '/users': 'pageUserManagement',
};

const ROLE_LABEL_KEYS = { admin: 'roleAdmin', waiter: 'roleWaiter', kitchen: 'roleKitchen' };
const roleBadgeColors = {
  admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  waiter: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  kitchen: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

// UI-04 parça 4/6: Okundu durumu sessionStorage'da (sunucu state yok)
const READ_KEY = 'restaflex_notifications_read';
// UI-04 polish: Dismiss (sil) durumu da client-side — bildirimler backend'de
// dinamik derive edildiği için sunucu tarafında "delete" yok. Kullanıcının
// tarafında saklanan id'ler bir sonraki API çekiminde tekrar görünmez.
const DISMISSED_KEY = 'restaflex_notifications_dismissed';

function loadIdSet(key) {
  try {
    const raw = sessionStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}
function persistIdSet(key, set) {
  try {
    sessionStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* quota/private — sessiz geç */
  }
}

const loadReadIds      = () => loadIdSet(READ_KEY);
const persistReadIds   = (set) => persistIdSet(READ_KEY, set);
const loadDismissedIds = () => loadIdSet(DISMISSED_KEY);
const persistDismissed = (set) => persistIdSet(DISMISSED_KEY, set);

// UI-04 parça 5/6: Yeni bildirim için beep (Web Audio, paketsiz)
function beep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 1040;
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
  } catch { /* autoplay engeli vs — sessiz geç */ }
  try {
    if (navigator.vibrate) navigator.vibrate(120); // mobil
  } catch { /* */ }
}

// UI-04 parça 2-3/6: Bildirim rozeti + açılır panel
// Named export — WaiterLayout da kullanabilir (UI-04 parça 6/6)
export function NotificationBell() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState(loadReadIds);
  const [dismissedIds, setDismissedIds] = useState(loadDismissedIds);
  const containerRef = useRef(null);
  const prevIdsRef = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const res = await notificationsAPI.getAll();
      const list = res.data.data || [];
      setNotifications(list);
      // Yeni id var mı?
      const currentIds = new Set(list.map(n => n.id));
      const isFirstLoad = prevIdsRef.current.size === 0;
      const hasNew = !isFirstLoad && [...currentIds].some(id => !prevIdsRef.current.has(id));
      if (hasNew) beep();
      prevIdsRef.current = currentIds;
    } catch {
      /* 401/403/500 — sessiz geç */
    }
  }, []);

  const markRead = useCallback((id) => {
    setReadIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      persistReadIds(next);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    const all = new Set([...readIds, ...notifications.map(n => n.id)]);
    persistReadIds(all);
    setReadIds(all);
  }, [readIds, notifications]);

  const dismiss = useCallback((id) => {
    setDismissedIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      persistDismissed(next);
      return next;
    });
  }, []);

  const dismissAllVisible = useCallback(() => {
    const visible = notifications.filter(n => !dismissedIds.has(n.id));
    if (visible.length === 0) return;
    const next = new Set([...dismissedIds, ...visible.map(n => n.id)]);
    persistDismissed(next);
    setDismissedIds(next);
  }, [notifications, dismissedIds]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000); // 1 dk safety net
    return () => clearInterval(interval);
  }, [load]);

  useSSE(({ type }) => {
    if (['tables', 'orders', 'kitchen', 'waiter_calls', 'stock', 'all'].includes(type)) load();
  });

  // Dış tıklama ile kapat
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const visible = notifications.filter(n => !dismissedIds.has(n.id));
  const unread = visible.filter(n => !readIds.has(n.id));
  const count = unread.length;
  const severityDot = {
    danger: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500',
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        title={t('tab_notifications')}
        aria-label={count > 0 ? `${t('tab_notifications')} (${count})` : t('tab_notifications')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="text-lg" aria-hidden="true">🔔</span>
        {count > 0 && (
          <>
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
              aria-hidden="true"
            >
              {count > 99 ? '99+' : count}
            </span>
            <span className="sr-only">{count} {t('a11yUnreadNotifications') || 'okunmamış bildirim'}</span>
          </>
        )}
      </button>
      {open && (
        <div role="menu" aria-label={t('tab_notifications')} className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="sticky top-0 px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
            <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm flex-1 min-w-0 truncate">
              {t('tab_notifications')} {count > 0 && <span className="text-xs text-gray-500 font-normal">({count} {t('notifUnreadCountSuffix')})</span>}
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              {count > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] text-blue-600 hover:text-blue-800 dark:text-blue-400 px-1.5 py-0.5"
                  title={t('notifMarkAllReadTitle')}
                >
                  {t('notifMarkAllReadShort')}
                </button>
              )}
              {visible.length > 0 && (
                <button
                  onClick={dismissAllVisible}
                  className="text-[11px] text-red-600 hover:text-red-800 dark:text-red-400 px-1.5 py-0.5"
                  title={t('notifDismissAllTitle')}
                >
                  {t('notifDismissAllShort')}
                </button>
              )}
            </div>
          </div>
          {visible.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('notifEmpty')}
              <div className="mt-2">
                <button
                  onClick={() => { setOpen(false); navigate('/notifications'); }}
                  className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 font-semibold"
                >{t('notifViewAll')}</button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {visible.slice(0, 8).map(n => {
                const isRead = readIds.has(n.id);
                return (
                  <li
                    key={n.id}
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => { markRead(n.id); if (n.link) navigate(n.link); setOpen(false); }}
                    className={`group relative px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer flex gap-3 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${isRead ? 'opacity-60' : ''}`}
                  >
                    <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${severityDot[n.severity] || 'bg-gray-400'}`}></span>
                    <div className="flex-1 min-w-0 pr-6">
                      <div className={`text-sm text-gray-800 dark:text-gray-100 ${isRead ? 'font-normal' : 'font-medium'}`}>{n.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{n.message}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                      aria-label={t('notifDismissOne')}
                      title={t('delete')}
                      className="absolute top-2 right-2 w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 opacity-70 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition-opacity text-sm"
                    >✕</button>
                  </li>
                );
              })}
            </ul>
          )}
          {visible.length > 0 && (
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-2 text-center">
              <button
                onClick={() => { setOpen(false); navigate('/notifications'); }}
                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 font-semibold"
                aria-label={`${t('notifViewAll')} (${visible.length})`}
              >
                {visible.length > 8 ? `${visible.length} → ${t('notifViewAll')}` : t('notifViewAll')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LanguageSwitcher() {
  const { lang, setLang, SUPPORTED_LANGUAGES, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);
  const current = SUPPORTED_LANGUAGES.find(l => l.code === lang) || SUPPORTED_LANGUAGES[0];
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="p-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        title={current.label}
        aria-label={current.label}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="hidden sm:inline text-xs font-semibold uppercase">{current.code}</span>
      </button>
      {open && (
        <div role="menu" aria-label="Language" className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          {SUPPORTED_LANGUAGES.map(l => (
            <button
              key={l.code}
              role="menuitem"
              onClick={() => { setLang(l.code); setOpen(false); }}
              aria-label={`${t('langSwitchTo') || 'Dile geç'}: ${l.label}`}
              aria-current={lang === l.code ? 'true' : undefined}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
                lang === l.code
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <span className="text-base leading-none" aria-hidden="true">{l.flag}</span>
              <span className="flex-1">{l.label}</span>
              {lang === l.code && <span className="text-xs">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function QuickActionsMenu() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);
  const allItems = [
    { icon: '🛒', label: t('quickActionNewOrder')    || 'Yeni Sipariş',  to: '/pos',       roles: ['admin', 'cashier'] },
    { icon: '🪑', label: t('quickActionNewTable')    || 'Yeni Masa',     to: '/tables',    roles: ['admin', 'waiter'] },
    { icon: '👥', label: t('quickActionNewCustomer') || 'Yeni Müşteri',  to: '/customers', roles: ['admin'] },
    { icon: '👤', label: t('quickActionNewStaff')    || 'Yeni Personel', to: '/users',     roles: ['admin'] },
  ];
  const items = allItems.filter(it => !user || it.roles.includes(user.role));
  if (items.length === 0) return null;
  const title = t('quickActionsTitle') || 'Hızlı İşlem';
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center text-xl leading-none font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
        title={title}
        aria-label={title}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        +
      </button>
      {open && (
        <div role="menu" aria-label={title} className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            {title}
          </div>
          {items.map(it => (
            <Link
              key={it.to}
              to={it.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              aria-label={it.label}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <span className="text-base leading-none" aria-hidden="true">{it.icon}</span>
              <span className="flex-1">{it.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const location = useLocation();
  const titleKey = PAGE_TITLE_KEYS[location.pathname];
  const title = titleKey ? t(titleKey) : 'RestaFlex';
  const activeOkcDeviceId = getActiveOkcDeviceId();

  return (
    <header role="banner" className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3 flex items-center justify-between">
      <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100 ml-10 md:ml-0">{title}</h1>
      <nav role="navigation" aria-label={t('headerNavLabel') || 'Header navigation'} className="flex items-center gap-2">
        {user && <OkcHealthIndicator deviceId={activeOkcDeviceId} />}
        {user && <QuickActionsMenu />}
        {user && <NotificationBell />}
        <LanguageSwitcher />
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          title={isDark ? t('themeLightMode') : t('themeDarkMode')}
          aria-label={t('themeToggleLabel') || 'Tema değiştir'}
          aria-pressed={isDark}
        >
          <span aria-hidden="true">{isDark ? '☀️' : '🌙'}</span>
        </button>
        {user && (
          <div className="flex items-center gap-2" role="group" aria-label={t('userProfileLabel') || 'Kullanıcı profili'}>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${roleBadgeColors[user.role]}`}>
              {ROLE_LABEL_KEYS[user.role] ? t(ROLE_LABEL_KEYS[user.role]) : user.role}
            </span>
            <span className="text-sm text-gray-700 dark:text-gray-300 hidden sm:block">{user.name}</span>
          </div>
        )}
      </nav>
    </header>
  );
}
